import { forwardRef, MediaHTMLAttributes, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import useVideoAutoPlay from './utils/useVideoAutoPlay';
import styles from './index.module.scss';
// import PictureInPicture from './plugins/PictureInPicture';

import EventEmitter from 'eventemitter3';
import ReactDOM from 'react-dom';
import { twMerge } from 'tailwind-merge';
import dynamic from 'next/dynamic';
import { CroppedImage } from '@weee-fe/module';

const PictureInPicture = dynamic(() => import('./plugins/PictureInPicture'), { ssr: false });

type IProps = MediaHTMLAttributes<HTMLVideoElement> & {
  /** 视频播放事件  */
  onPlay?: () => void;
  /** 播放暂停事件  */
  onPause?: () => void;
  onEnded?: () => void;
  onPlayIconClick?: (e: Event) => void;
  /**
   *点击关闭按钮时触发
   * @param {Boolean} fullScreen 是否全屏
   * @param {Boolean}  isPlaying 是否在播放状态
   * */
  onCloseClick?: (fullScreen: boolean, isPlaying: boolean, isEnded: boolean) => void;
  /** 视频封面图片  */
  poster: string;
  /** 画中画  */
  pip?: boolean;
  controlPanel?: {
    showPlayIcon?: boolean;
    showMuteIcon?: boolean;
  };
  defaultProps?: {
    showPlayIcon?: boolean;
    showMuteIcon?: boolean;
  };
  className?: string;
  muteIconUrl?: string;
  soundIconUrl?: string;
  animation?: boolean;
  playClassName?: string;
  videoClassName?: string;
  posterClassName?: string;
  muteClassName?: string;
};
export interface WeeeVideoRefProps {
  playVideo?: () => void;
  autoPlayVideo?: () => void;
  events?: EventEmitter;
  pauseVideo?: () => void;
  resetVideo?: () => void;
  setShowPlayIcon?: (show: boolean) => void;
  setShowMuteIcon?: (show: boolean) => void;
  setIsMuted?: (isMuted: boolean) => void;
  requestPictureInPicture?: () => void;
  exitPictureInPicture?: () => void;
  parentElement: HTMLDivElement;
  video: HTMLVideoElement;
}
const WeeeVideo = forwardRef<WeeeVideoRefProps, IProps>(
  (
    {
      className,
      src: videoSrc,
      autoPlay = false,
      loop = false,
      poster,
      pip = false,
      onPlay,
      onPause,
      onEnded,
      controlPanel = {},
      defaultProps = {},
      onPlayIconClick,
      muteIconUrl,
      soundIconUrl,
      onCloseClick,
      animation = true,
      videoClassName,
      playClassName,
      posterClassName,
      muteClassName,
      ...props
    }: IProps,
    componentRef
  ) => {
    controlPanel = {
      showPlayIcon: true,
      showMuteIcon: true,
      ...controlPanel
    };
    defaultProps = {
      showPlayIcon: false,
      showMuteIcon: true,
      ...defaultProps
    };

    const videoRef = useRef<HTMLVideoElement>(null);
    const wrapperEl = useRef<HTMLDivElement>(null);
    const [showPoster, setShowPoster] = useState(true);
    const [isMuted, setIsMuted] = useState(true);
    const [showPlayIcon, setShowPlayIcon] = useState(defaultProps.showPlayIcon ?? false);
    const [showMuteIcon, setShowMuteIcon] = useState(defaultProps.showMuteIcon ?? false);
    const [showLoading, setShowLoading] = useState(false);
    const [showPip, setShowPip] = useState(false);
    const { canAutoPlay, playVideoViaInternalEvent } = useVideoAutoPlay();
    const firstLoaded = useRef(false);
    const events = useRef<EventEmitter>(new EventEmitter());

    useEffect(() => {
      videoRef.current && (videoRef.current.muted = isMuted);
    }, [isMuted]);

    const handleVideoPlayFail = () => {
      setShowPlayIcon(true);
      setShowLoading(false);
    };
    const playVideoByUser = () => {
      playVideoViaInternalEvent(videoRef.current, _err => handleVideoPlayFail());
    };
    /** 播放视频  */
    const autoPlayHandler = async () => {
      if (await canAutoPlay()) playVideoByUser();
      else handleVideoPlayFail();
    };

    useEffect(() => {
      let el: HTMLVideoElement = videoRef.current;
      if (!el) return;
      if (autoPlay) {
        autoPlayHandler();
      }
      el.addEventListener('volumechange', e => {
        setIsMuted(el.muted);
        events.current.emit('volumechange', e, el.muted);
      });
    }, [videoRef.current]);

    const requestPictureInPicture = () => {
      setShowPip(true);
    };
    const exitPictureInPicture = () => {
      setShowPip(false);
    };

    /**
     * 1. 没配置pip并且非loop时，显示封面，显示播放icon
     * 2. 如果是小窗画中画，非loop视频播放完成后，自动关闭小窗画中画
     * 3. 如果是全屏时，跳过视频结束逻辑
     **/
    const videoOnEnded = (fullScreen?: boolean) => {
      if (showPip && fullScreen === undefined) return;
      if (!loop && (fullScreen === false || !showPip)) {
        // 不是无限播放，则播放完展示原始图片
        setShowPlayIcon(true);
        setShowPoster(true);
        exitPictureInPicture();
        onEnded?.();
      }
    };

    const PIP = useMemo(() => {
      if (showPip) {
        let addCartEl: HTMLDivElement = document.querySelector('.cart');
        let bottom = window.innerHeight - (addCartEl?.offsetTop || 0);
        return ReactDOM.createPortal(
          <PictureInPicture
            videoElement={videoRef.current}
            muted={isMuted}
            onEnd={videoOnEnded}
            onClose={(fullScreen, isPlaying, isEnded) => {
              exitPictureInPicture();
              onCloseClick?.(fullScreen, isPlaying, isEnded);
            }}
            loop={loop}
            margin={{ bottom }}
          />,
          document.body
        );
      } else {
        wrapperEl.current?.appendChild?.(videoRef.current);
        return null;
      }
    }, [showPip]);

    useImperativeHandle(
      componentRef,
      () => ({
        playVideo: playVideoByUser,
        autoPlayVideo: autoPlayHandler,
        pauseVideo: () => {
          videoRef.current.pause();
        },
        resetVideo: () => {
          videoRef.current.currentTime && videoRef.current?.load();
          setShowPoster(true);
          setShowPlayIcon(true);
        },
        requestPictureInPicture,
        exitPictureInPicture,
        parentElement: wrapperEl.current,
        setShowPlayIcon: (show: boolean) => {
          setShowPlayIcon(show);
        },
        events: events.current,
        setShowMuteIcon,
        setShowPoster,
        setIsMuted,
        video: videoRef.current
      }),
      [videoSrc]
    );

    return (
      <>
        <div className={twMerge('overflow-hidden flex items-center [&_video]:w-full', className)} ref={wrapperEl}>
          <CroppedImage
            wrapperClassName="flex items-center"
            url={poster}
            style={{ display: showPoster ? 'block' : 'none' }}
            className={twMerge(
              'object-contain w-full',
              animation && (showPoster ? styles['fade-in'] : styles['fade-out']),
              posterClassName
            )}
            showLoadingPlaceholder={false}
            showErrorPlaceholder={false}
            data-type="poster"
            alt="video cover"
          />
          <video
            data-type="video"
            style={{ display: !showPoster ? 'block' : 'none' }}
            className={twMerge(
              'pointer-events-none w-screen h-[100vw]',
              showPip && 'w-[100px] h-[100px]',
              `${animation && (!showPoster ? styles['fade-in'] : styles['fade-out'])}`,
              videoClassName
            )}
            muted
            ref={videoRef}
            loop={loop}
            controlsList="nodownload nofullscreen"
            disablePictureInPicture={true}
            preload={'none'}
            x5-video-player-type="h5"
            x5-video-orientation="portraint"
            x5-video-player-fullscreen="false"
            playsInline={true}
            x5-playsinline="true"
            webkit-playsinline="true"
            onPlay={event => {
              setShowPlayIcon(false);
              if (firstLoaded.current) {
                setShowPoster(false);
              }
              onPlay?.(event);
              events.current.emit('play', event);
            }}
            onCanPlay={event => {
              setShowLoading(false);
              if (!firstLoaded.current) {
                setShowPoster(false);
                firstLoaded.current = true;
              }
              events.current.emit('canplay', event);
            }}
            onLoadedData={() => setShowLoading(false)}
            onWaiting={() => setShowLoading(true)}
            onPause={event => {
              onPause?.();
              events.current.emit('pause', event);
            }}
            onEnded={event => {
              videoOnEnded();
              events.current.emit('ended', event);
            }}
            onError={handleVideoPlayFail}
            src={videoSrc}
            {...props}
          />
          {controlPanel.showPlayIcon && showPlayIcon && (
            <img
              data-type="play-icon"
              src={require('./images/play.svg')}
              alt=""
              className={
                playClassName
                  ? playClassName
                  : 'absolute w-[58px] h-[58px] z-[1] left-2/4 top-2/4 -translate-x-1/2 -translate-y-1/2'
              }
              onClick={e => {
                e.stopPropagation();
                videoRef.current.play();
                onPlayIconClick?.((e as unknown) as Event);
              }}
            />
          )}
          {controlPanel.showMuteIcon && showMuteIcon && (
            <i
              style={{
                backgroundImage: `url(${
                  isMuted ? muteIconUrl || require('./images/muted.svg') : soundIconUrl || require('./images/sound.svg')
                })`
              }}
              data-type="mute-icon"
              className={twMerge(
                'absolute w-[38px] h-[22px] z-[1] bg-no-repeat bg-contain right-4 bottom-4',
                muteClassName
              )}
              onClick={e => {
                e.preventDefault();
                videoRef.current.muted = !isMuted;
              }}
            />
          )}
          {showLoading && (
            <img
              className="absolute -translate-x-2/4 -translate-y-2/4 w-7 z-[1] h-7 left-2/4 top-2/4;"
              src={require('./images/loading.svg')}
              alt=""
            />
          )}
          {showPip && PIP}
        </div>
      </>
    );
  }
);
WeeeVideo.displayName = 'WeeeVideo';
export default WeeeVideo;
